import { useCallback, useEffect, useState } from 'react';
import { ADMIN_DEFAULT_PAGE, ADMIN_PATHS, type AdminPage } from './admin-paths';
import { SELLER_DEFAULT_PAGE, SELLER_PATHS, type SellerPage } from './seller-paths';

/**
 * Routing, in one file and with no dependency.
 *
 * **Why now.** Views were state, so every reload landed on the feed — and an auction is
 * the first thing in this product that is genuinely a URL worth sharing. A link to a
 * running auction is how a buyer arrives at one; a seller sending "look at this" is the
 * whole distribution model for passive commerce.
 *
 * **Why not a router library.** Five flat routes, one parameter between them. The edge
 * already does the hard part — `EdgeStack`'s CloudFront function rewrites anything
 * without a file extension to `index.html`, so deep links resolve before this code runs —
 * and React Native will bring its own navigation rather than share this. A dependency
 * here would be larger than the thing it replaces, and Compass says a new library is an
 * Office Hours conversation, not a quiet import.
 *
 * **When to replace it with one** (write it down now, so the decision has a trigger
 * rather than a mood): nested routes, route-level data loading, or more than one
 * parameter in a path. Any of those and this stops being small.
 *
 * **The admin console** (D46 addendum, 2026-10-08) adds multi-segment paths
 * (`/admin/settings/one`) but no parameter and no data loading: each page is a literal
 * entry in `ADMIN_PATHS`, so it stays on this side of the trigger. **The seller dashboard**
 * (D46 addendum, 2026-10-12) adds twenty-seven more of the same shape, and is deliberately
 * `/seller/dashboard/*` rather than `/admin/…` so that impersonation, when it lands, takes
 * the seller from the session instead of from a path parameter — which is what would push
 * this file past its own trigger. See `seller-paths.ts`.
 */

export type Route =
  | { name: 'feed' }
  | { name: 'auction'; auctionId: string }
  /** A live show, as a viewer. The other link people paste. */
  | { name: 'show'; showId: string }
  /** The seller's broadcast console: the list, and one show's desk. */
  | { name: 'studio' }
  | { name: 'studioShow'; showId: string }
  | { name: 'sell' }
  /** The CSV import's team harness (D55, O32) — linked from nowhere; sellers import on the phone. */
  | { name: 'importHarness' }
  /** The buyer's saved cards. `/cards` because it is a destination, not a modal. */
  | { name: 'cards' }
  | { name: 'design' }
  /** The staff admin console (O32: the web is the internal admin tool). A closed set of pages. */
  | { name: 'admin'; page: AdminPage }
  /** The seller dashboard's shell — staff-gated, like the console, by `adminAccess()`. */
  | { name: 'sellerDash'; page: SellerPage }
  /** Where Stripe returns a seller — `complete` and `resume` land in the same place. */
  | { name: 'sellerReturn' };

/** `/a/{id}` rather than `/auctions/{id}`: this is a link people paste into messages. */
const AUCTION = /^\/a\/([0-9a-fA-F-]{36})$/;
/** `/s/{id}` for the same reason. A show is the other shareable thing in the product. */
const SHOW = /^\/s\/([0-9a-fA-F-]{36})$/;
/** Not `/s/` — the studio is a workplace, not a link anybody sends. */
const STUDIO_SHOW = /^\/studio\/([0-9a-fA-F-]{36})$/;

export function parseRoute(pathname: string): Route {
  const path = pathname.replace(/\/+$/, '') || '/';
  const auction = AUCTION.exec(path);
  if (auction?.[1]) return { name: 'auction', auctionId: auction[1].toLowerCase() };
  const show = SHOW.exec(path);
  if (show?.[1]) return { name: 'show', showId: show[1].toLowerCase() };
  const studioShow = STUDIO_SHOW.exec(path);
  if (studioShow?.[1]) return { name: 'studioShow', showId: studioShow[1].toLowerCase() };
  if (path === '/studio') return { name: 'studio' };
  if (path === '/design') return { name: 'design' };
  // Before the three `/sell` branches. They match `/sell`, `/sell/import` and
  // `/sell/onboarding…` specifically, so `/seller/…` never reached them — but ordering it
  // first means a future `/sell` prefix match cannot quietly swallow the dashboard.
  if (path === '/seller/dashboard' || path.startsWith('/seller/dashboard/'))
    return { name: 'sellerDash', page: sellerDashPage(path) };
  if (path.startsWith('/sell/onboarding')) return { name: 'sellerReturn' };
  if (path === '/sell/import') return { name: 'importHarness' };
  if (path === '/sell') return { name: 'sell' };
  if (path === '/cards') return { name: 'cards' };
  if (path === '/admin' || path.startsWith('/admin/')) return { name: 'admin', page: adminPage(path) };
  // Anything unrecognized is the feed rather than a 404 screen: the only way to reach an
  // unknown path here is an old link or a typo, and both are better answered with the
  // thing the app is for.
  return { name: 'feed' };
}

/**
 * An admin path's page. An unknown one inside `/admin` is the console's default page,
 * not the feed: a stale link inside a staff tool is better answered with the tool.
 */
function adminPage(path: string): AdminPage {
  const match = Object.entries(ADMIN_PATHS).find(([, known]) => known === path);
  return match ? (match[0] as AdminPage) : ADMIN_DEFAULT_PAGE;
}

/** The same rule for the seller dashboard: a stale link inside it lands on its home. */
function sellerDashPage(path: string): SellerPage {
  const match = Object.entries(SELLER_PATHS).find(([, known]) => known === path);
  return match ? (match[0] as SellerPage) : SELLER_DEFAULT_PAGE;
}

export function pathFor(route: Route): string {
  switch (route.name) {
    case 'auction':
      return `/a/${route.auctionId}`;
    case 'show':
      return `/s/${route.showId}`;
    case 'studio':
      return '/studio';
    case 'studioShow':
      return `/studio/${route.showId}`;
    case 'sell':
    case 'sellerReturn':
      return '/sell';
    case 'cards':
      return '/cards';
    case 'importHarness':
      return '/sell/import';
    case 'design':
      return '/design';
    case 'admin':
      return ADMIN_PATHS[route.page];
    case 'sellerDash':
      return SELLER_PATHS[route.page];
    case 'feed':
      return '/';
  }
}

/**
 * Whether the address bar should be rewritten to the route it resolved to: an unknown
 * `/admin/…` path renders the default page, and a link copied from it should carry that
 * page, not the stale path. A trailing slash alone is not a difference.
 */
export function needsCanonical(pathname: string, route: Route): boolean {
  return (pathname.replace(/\/+$/, '') || '/') !== pathFor(route);
}

/**
 * The current route, and how to change it.
 *
 * `popstate` is the half people forget: without it the browser's own back button changes
 * the URL and nothing else, which is worse than having no routing at all.
 */
export function useRoute(): { route: Route; navigate: (to: Route, options?: { replace?: boolean }) => void } {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));

  useEffect(() => {
    const onPop = () => setRoute(parseRoute(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Stable across renders, so an effect that navigates can depend on it honestly rather
  // than lying about its dependencies — and so depending on it does not re-run that
  // effect on every render.
  const navigate = useCallback((to: Route, options?: { replace?: boolean }) => {
    const path = pathFor(to);
    // `replace` for the two redirect landings: a single-use OAuth code and a Stripe
    // return should not be entries somebody can navigate back into.
    if (options?.replace) window.history.replaceState({}, '', path);
    else window.history.pushState({}, '', path);
    setRoute(to);
    window.scrollTo(0, 0);
  }, []);

  return { route, navigate };
}
